/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { ScanDropzone } from "./ScanDropzone";
import { ScanProgress } from "./ScanProgress";
import { ScanReview } from "./ScanReview";
import { useScan } from "./useScan";
import { suggestedKind, type ScanKind, type ScanResult } from "./types";

type Props = {
  /** What the page expects (sets the AI's hint and the main button). */
  kind: ScanKind;
  onClose: () => void;
  /** Open the scan in an editor — any kind; the page routes other kinds. */
  onOpen: (kind: ScanKind, result: ScanResult) => void;
};

const KINDS: ScanKind[] = ["receipt", "invoice", "order"];
const KIND_ICON: Record<ScanKind, string> = {
  receipt: "local_shipping",
  invoice: "request_quote",
  order: "shopping_cart",
};

// Scan a delivery note, invoice or order confirmation: pick → read → review → open.
export const ScanDialog = defineComponent<Props>({
  name: "ScanDialog",
  props: ["kind", "onClose", "onOpen"],
  setup(props) {
    const { t } = useLocalization();
    const files = ref<File[]>([]);
    const kind = ref<ScanKind>(props.kind);
    const scan = useScan();

    const errorText = (e: string) =>
      e === "unreadable_image" ? t("inventory.scan.error_image") : e === "no_files" ? t("inventory.scan.error_no_files") : e;

    const renderBody = () => {
      if (scan.step.value) {
        return <ScanProgress step={scan.step.value} preview={scan.prepared.value?.pages[0] ?? null} />;
      }
      if (scan.result.value) {
        return <ScanReview result={scan.result.value} pages={scan.prepared.value?.pages ?? []} />;
      }
      return (
        <div class="space-y-4">
          <div>
            <div class="mb-2 text-sm font-medium">{t("inventory.scan.what_is_it")}</div>
            <div class="grid grid-cols-3 gap-2">
              {KINDS.map((k) => (
                <button
                  key={k}
                  class={[
                    "flex flex-col items-center gap-1 rounded-xl border p-3 text-sm transition",
                    kind.value === k ? "border-primary bg-primary/10 font-semibold text-primary" : "border-base-300 hover:border-base-content/30",
                  ].join(" ")}
                  onClick={() => (kind.value = k)}
                >
                  <span class="material-symbols-outlined">{KIND_ICON[k]}</span>
                  {t(`inventory.scan.kind_${k}`)}
                </button>
              ))}
            </div>
          </div>
          <ScanDropzone files={files.value} onChange={(f: File[]) => (files.value = f)} />
          {scan.error.value && (
            <div class="alert alert-error py-2 text-sm">
              <span class="material-symbols-outlined">error</span>
              <span>{errorText(scan.error.value)}</span>
            </div>
          )}
          <p class="flex items-center gap-1 text-xs text-base-content/50">
            <span class="material-symbols-outlined text-sm">auto_awesome</span>
            {t("inventory.scan.ai_note")}
          </p>
        </div>
      );
    };

    const renderButtons = () => {
      const result = scan.result.value;
      const reading = scan.step.value != null;
      return (
        <div class="border-t p-4">
          <div class="flex flex-wrap justify-between gap-2">
            <button class="btn btn-neutral" disabled={reading} onClick={() => props.onClose()}>
              {t("general.words.cancel")}
            </button>
            {result ? (
              <div class="flex flex-wrap gap-2">
                <button
                  class="btn btn-ghost"
                  onClick={() => {
                    scan.reset();
                    files.value = [];
                  }}
                >
                  {t("inventory.scan.scan_another")}
                </button>
                {KINDS.filter((k) => k !== suggestedKind(result, kind.value)).map((k) => (
                  <button key={k} class="btn btn-ghost" onClick={() => props.onOpen(k, result)}>
                    {t(`inventory.scan.open_as_${k}`)}
                  </button>
                ))}
                <button class="btn btn-primary" onClick={() => props.onOpen(suggestedKind(result, kind.value), result)}>
                  <span class="material-symbols-outlined text-base">arrow_forward</span>
                  {t(`inventory.scan.open_as_${suggestedKind(result, kind.value)}`)}
                </button>
              </div>
            ) : (
              <button
                class="btn btn-primary"
                disabled={reading || files.value.length === 0}
                onClick={() => scan.run(files.value, kind.value)}
              >
                <span class="material-symbols-outlined text-base">auto_awesome</span>
                {t("inventory.scan.read")}
              </button>
            )}
          </div>
        </div>
      );
    };

    return () => (
      <LemmonModal
        title={t("inventory.scan.title")}
        onClose={() => {
          if (!scan.step.value) props.onClose();
        }}
        renderButtons={renderButtons}
      >
        <div class="p-6">{renderBody()}</div>
      </LemmonModal>
    );
  },
});
